import {
  APIPresentationAssetAttributes,
  PresentationReadPermissions,
  PresentationWritePermissions,
} from '@ardoq/api-types';
import { PermissionContext } from '../types';
import { permissionsOperations } from '../permissionsOperations';

export const isPresentationOwner = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes
): boolean => {
  return presentation.createdBy === permissionContext.user?._id;
};

export const isPublic = (
  presentation?: APIPresentationAssetAttributes
): boolean => presentation?.readAccess === PresentationReadPermissions.ALL;

export const canEditPresentation = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes
): boolean => {
  if (
    !presentation ||
    !permissionsOperations.isLoadedPermissionContext(permissionContext) ||
    !permissionsOperations.isOrgWriter(permissionContext.user)
  ) {
    return false;
  }

  if (presentation.writeAccess === PresentationWritePermissions.ME) {
    return (
      isPresentationOwner(permissionContext, presentation) ||
      permissionsOperations.isOrgAdmin(permissionContext.user)
    );
  }
  return true;
};

const areWritePermissionsLimitedToCurrentUser = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes
): boolean => {
  return (
    presentation?.writeAccess === PresentationWritePermissions.ME &&
    isPresentationOwner(permissionContext, presentation)
  );
};

export const canCurrentUserOpenInApp = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes
): boolean => {
  const authorizedSlides = presentation.authorizedSlides ?? [];
  const limitedToUser = areWritePermissionsLimitedToCurrentUser(
    permissionContext,
    presentation
  );
  return (
    presentation.writeAccess === PresentationWritePermissions.WS ||
    limitedToUser ||
    (authorizedSlides && authorizedSlides.length > 0)
  );
};

export const canCurrentUserEditAccessRights = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes,
  canCurrentUserWrite: boolean,
  currentUserIsOrgAdmin: boolean
): boolean => {
  if (currentUserIsOrgAdmin) return true;
  const canEdit =
    canCurrentUserWrite &&
    !isPublic(presentation) &&
    isPresentationOwner(permissionContext, presentation);
  return canEdit;
};

export const canCopyPresentation = (
  permissionContext: PermissionContext,
  presentation: APIPresentationAssetAttributes
): boolean => {
  const userIsOrgWriter = Boolean(
    permissionContext.user &&
    permissionsOperations.isOrgWriter(permissionContext.user)
  );

  return (
    canCurrentUserOpenInApp(permissionContext, presentation) && userIsOrgWriter
  );
};
